import React, { createContext, useContext, useState, useCallback } from 'react';

const SignUpContext = createContext(null);

const initialState = {
  // Invitation link (from URL)
  invitationLink: '',

  // User account (from registration)
  email: '',
  password: '',
  userId: null,
  accessToken: null,
  refreshToken: null,

  // Account type
  accountType: '', // 'SELLER_PF' or 'SELLER_PJ'

  // Personal info
  person: {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    taxpayer_id: '', // CPF (numbers only)
    birthdate: '',
    mother_name: '',
    address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      postal_code: '', // numbers only
      country_code: 'BR'
    }
  },

  // Business info
  category: '',
  categoryLabel: '',
  mcc: '',
  mccLabel: '',
  statement_descriptor: '',
  revenue: '',

  // Bank account
  bank_account: {
    number: '',
    agency: '',
    code: '',
    type: '' // 'CHECKING_ACCOUNT' or 'SAVINGS_ACCOUNT'
  },

  // Company (PJ only)
  company: {
    name: '',
    email: '',
    phone: '',
    tax_id: '', // CNPJ (numbers only)
    opening_date: '',
    address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      postal_code: '',
      country_code: 'BR'
    }
  }
};

export const SignUpProvider = ({ children }) => {
  const [formData, setFormData] = useState(initialState);
  const [currentStep, setCurrentStep] = useState(0);

  // Update a single field at root level
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Update person data
  const updatePerson = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      person: {
        ...prev.person,
        [field]: value
      }
    }));
  }, []);

  // Update person address
  const updatePersonAddress = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      person: {
        ...prev.person,
        address: {
          ...prev.person.address,
          [field]: value
        }
      }
    }));
  }, []);

  // Update bank account data
  const updateBankAccount = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      bank_account: {
        ...prev.bank_account,
        [field]: value
      }
    }));
  }, []);

  // Update company data
  const updateCompany = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      company: {
        ...prev.company,
        [field]: value
      }
    }));
  }, []);

  // Update company address
  const updateCompanyAddress = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      company: {
        ...prev.company,
        address: {
          ...prev.company.address,
          [field]: value
        }
      }
    }));
  }, []);

  // Set user data after registration
  const setUserData = useCallback((userData) => {
    setFormData(prev => ({
      ...prev,
      userId: userData.user.id,
      accessToken: userData.access,
      refreshToken: userData.refresh,
      email: userData.user.email,
      // Pre-fill person email with user email
      person: {
        ...prev.person,
        email: userData.user.email
      }
    }));
  }, []);

  // Build the seller payload for API submission
  const buildSellerPayload = useCallback(() => {
    const payload = {
      type: formData.accountType,
      email: formData.person.email,
      revenue: formData.revenue,
      category: formData.category,
      mcc: formData.mcc,
      statement_descriptor: formData.statement_descriptor,
      invitation_link: formData.invitationLink || undefined,
      person: {
        first_name: formData.person.first_name,
        last_name: formData.person.last_name,
        email: formData.person.email,
        phone: formData.person.phone,
        taxpayer_id: formData.person.taxpayer_id.replace(/\D/g, ''),
        birthdate: formData.person.birthdate,
        mother_name: formData.person.mother_name,
        address: {
          street: formData.person.address.street,
          number: formData.person.address.number || null,
          complement: formData.person.address.complement || null,
          neighborhood: formData.person.address.neighborhood,
          city: formData.person.address.city,
          state: formData.person.address.state,
          postal_code: formData.person.address.postal_code.replace(/\D/g, ''),
          country_code: 'BR'
        }
      },
      bank_account: {
        number: formData.bank_account.number,
        agency: formData.bank_account.agency,
        code: formData.bank_account.code.replace(/^0+/, ''),
        type: formData.bank_account.type
      },
      company: null
    };

    // Add company data for SELLER_PJ
    if (formData.accountType === 'SELLER_PJ') {
      payload.company = {
        name: formData.company.name,
        email: formData.company.email,
        phone: formData.company.phone,
        tax_id: formData.company.tax_id.replace(/\D/g, ''),
        opening_date: formData.company.opening_date || null,
        address: {
          street: formData.company.address.street,
          number: formData.company.address.number || null,
          complement: formData.company.address.complement || null,
          neighborhood: formData.company.address.neighborhood,
          city: formData.company.address.city,
          state: formData.company.address.state,
          postal_code: formData.company.address.postal_code.replace(/\D/g, ''),
          country_code: 'BR'
        }
      };
    }

    return payload;
  }, [formData]);

  // Navigation
  const nextStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(initialState);
    setCurrentStep(0);
  }, []);

  const value = {
    formData,
    currentStep,
    updateField,
    updatePerson,
    updatePersonAddress,
    updateBankAccount,
    updateCompany,
    updateCompanyAddress,
    setUserData,
    buildSellerPayload,
    nextStep,
    prevStep,
    goToStep,
    resetForm
  };

  return (
    <SignUpContext.Provider value={value}>
      {children}
    </SignUpContext.Provider>
  );
};

export const useSignUp = () => {
  const context = useContext(SignUpContext);
  if (!context) {
    throw new Error('useSignUp must be used within a SignUpProvider');
  }
  return context;
};

export default SignUpContext;
